import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { IJsonApiOrderCoupon, IPagedResponse, OrderCoupon, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save orderCoupon data.
 */
@Injectable()
export class OrderCouponService {
    public static INCLUDE_PRODUCT_TYPES: string = 'productTypes';
    public static INCLUDE_EVENTS: string = 'events';
    public static INCLUDE_CREATED_BY_USER: string = 'createdByUserAccount';

    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all orderCoupons for the specified event
     */
    public getIndexListResponse(
        includes: string[] = [],
        queryParams: string[] = [],
        displaySpinner: boolean = true,
    ): Observable<PagedResponse> {
        const apiRequestUrl: string = this.getApiUrl(null, includes, queryParams);
        return this.getIndexListResponseByUrl(apiRequestUrl, displaySpinner);
    }

    /**
     * Get orderCoupons by a url. This is used for paging through orderCoupons since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(
        url: string,
        displaySpinner: boolean = true,
    ): Observable<PagedResponse> {
        return this.http.get(url, displaySpinner)
            .pipe(map((response: IPagedResponse) => {
                const json: IPagedResponse = response;
                const orderCoupons: OrderCoupon[] =
                    json.data.map((d: any) => (new OrderCoupon()).deserialize(d, json.included));

                return new PagedResponse(
                    json.links.first,
                    json.links.last,
                    json.links.prev,
                    json.links.next,
                    json.meta.total,
                    orderCoupons,
                );
            }));
    }

    /**
     * Get a single orderCoupon's data
     */
    public get(orderCouponId: string, includes: string[] = null, queryParams: string[] = []): Observable<OrderCoupon> {
        const apiRequestUrl: string = this.getApiUrl(orderCouponId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new OrderCoupon().deserialize(response, response.included))));
    }

    /**
     * Save an orderCoupon
     */
    public save(coupon: OrderCoupon, includes: string[] = []): Observable<OrderCoupon> {
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(coupon.id, includes);

        if (coupon.id) {
            // Update existing coupon
            response = this.http.patch(apiRequestUrl, coupon.serialize());
        } else {
            // Add a new coupon
            response = this.http.post(apiRequestUrl, coupon.serialize());
        }

        return response.pipe(
            map((response: IJsonApiResponse) => (new OrderCoupon()).deserialize(response, response.included)),
        );
    }

    /**
     * Generate multiple orderCoupons
     */
    public generate(
        coupon: OrderCoupon,
        prefix: string,
        generateCount: number,
        includes: string[] = [],
    ): Observable<OrderCoupon[]> {
        const json: IJsonApiOrderCoupon = coupon.serialize();
        delete json.data.attributes.code;
        json.data.attributes.codePrefix = prefix;
        json.data.attributes.generateCount = parseFloat((generateCount || '0').toString());

        return this.http.post(this.getApiUrl('generate/'), json)
            .pipe(
                map((response: IJsonApiResponse) => {
                    const bodyData: any = response.data || [];
                    return bodyData.map((data: any) => new OrderCoupon().deserialize(data, response.included));
                }),
            );
    }

    /**
     * Delete the specified orderCoupon.
     */
    public delete(orderCouponId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(orderCouponId, null);
        return this.http.delete(apiRequestUrl).pipe(map(() => true));
    }

    public exportToCsv(eventId: string): void {
        location.href = this.getApiUrl('csv-download', null, [`eventId=${eventId}`]);
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(orderCouponId: string, includes?: string[], queryParams?: string[]): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/order-coupons/`;

        if (orderCouponId) {
            url += orderCouponId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
